// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.

import type { Comparator, SemVer } from "./types.ts";
import { isWildcardComparator } from "./_shared.ts";
import { compare } from "./compare.ts";

function testComparator(version: SemVer, comparator: Comparator): boolean {
  if (isWildcardComparator(comparator)) {
    return true;
  }
  const cmp = compare(version, comparator);
  switch (comparator.operator) {
    case "=":
    case undefined: {
      return cmp === 0;
    }
    case "!=": {
      return cmp !== 0;
    }
    case ">": {
      return cmp > 0;
    }
    case "<": {
      return cmp < 0;
    }
    case ">=": {
      return cmp >= 0;
    }
    case "<=": {
      return cmp <= 0;
    }
  }
}

export function testComparatorSet(
  version: SemVer,
  set: Comparator[],
): boolean {
  for (const comparator of set) {
    if (!testComparator(version, comparator)) {
      return false;
    }
  }
  if (version.prerelease && version.prerelease.length > 0) {
    // Find the comparator that is allowed to have prereleases
    // For example, ^1.2.3-pr.1 desugars to >=1.2.3-pr.1 <2.0.0
    // That should allow `1.2.3-pr.2` to pass.
    // However, `1.2.4-alpha.notready` should NOT be allowed,
    // even though it's within the range set by the comparators.
    for (const comparator of set) {
      if (isWildcardComparator(comparator)) {
        continue;
      }
      const { major, minor, patch, prerelease } = comparator;
      if (prerelease && prerelease.length > 0) {
        if (
          version.major === major && version.minor === minor &&
          version.patch === patch
        ) {
          return true;
        }
      }
    }
    return false;
  }
  return true;
}

// denoCacheMetadata={"headers":{"x-guploader-uploadid":"AHmUCY3W9tuG5c0Un8nEvnkKyuhvZh26RozUkMsOiWWEr-yOJYJr2LRxB_kmv5yHmc9pIHcm-e4","via":"1.1 google","accept-ranges":"bytes","etag":"\"a5a7de9348b11a340e175f5459f7bad6\"","access-control-allow-origin":"*","access-control-expose-headers":"*","x-content-type-options":"nosniff","x-goog-stored-content-encoding":"identity","x-goog-generation":"1725446501890751","date":"Fri, 08 Nov 2024 09:36:15 GMT","expires":"Sat, 08 Nov 2025 09:36:15 GMT","last-modified":"Wed, 04 Sep 2024 10:41:41 GMT","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-jsr-cache-status":"hit","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","content-length":"1730","server":"UploadServer","x-goog-hash":"crc32c=fUjjeQ==,md5=pafek0ixGjQOF19UWfe61g==","age":"2026","content-type":"text/typescript","x-goog-stored-content-length":"1730","x-goog-storage-class":"STANDARD","cache-control":"public, max-age=31536000, immutable","x-jsr-cache-id":"IAD","cross-origin-resource-policy":"cross-origin","x-goog-metageneration":"1","x-robots-tag":"noindex"},"url":"https://jsr.io/@std/semver/1.0.3/_test_comparator_set.ts","time":1731060601}